import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  Grid, Box, Typography, Switch, Autocomplete, CircularProgress, Chip, Alert, Paper,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BusinessIcon from '@mui/icons-material/Business';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useBankAccounts } from '../hooks/useLookups';
import { expenseService } from '../services/expense.service';
import { filterWithAddNew } from '../utils/inlineContactCreate';
import AddCustomerModal from './customers/AddCustomerModal';

type ExpenseTarget = 'vehicle' | 'overhead' | 'purchase_order';

interface QuickAddExpenseModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function QuickAddExpenseModal({ open, onClose, onSuccess }: QuickAddExpenseModalProps) {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.id;
  const bankAccounts = useBankAccounts();

  const [target, setTarget] = useState<ExpenseTarget | ''>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);

  // New-supplier modal
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [supplierPrefill, setSupplierPrefill] = useState('');

  // OCR state
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrMatch, setOcrMatch] = useState<{ supplier?: string; vehicle?: string } | null>(null);
  const [ocrNote, setOcrNote] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState({
    vehicle_id: '',
    category: '',
    description: '',
    provider: '',
    amount: '',
    gst_inclusive: false,
    gst_amount: '0',
    payment_method: 'EFT',
    bank_account: '',
    expense_date: new Date().toISOString().split('T')[0],
    reference: '',
    status: 'pending',
  });

  useEffect(() => {
    if (open && orgId) {
      loadData();
    }
  }, [open, orgId]);

  const loadData = async () => {
    await loadCategories();
    await loadSuppliers();

    try {
      const { data: vehs } = await supabase.from('vehicles').select('id, year, make, model, stock_number').eq('organization_id', orgId).eq('status', 'available').order('created_at', { ascending: false }).limit(100);
      setVehicles((vehs || []).map(v => ({ id: v.id, label: `${v.year} ${v.make} ${v.model} (${v.stock_number || 'N/A'})` })));
    } catch { setVehicles([]); }
  };

  const loadCategories = async () => {
    try {
      const { data: cats } = await supabase
        .from('expense_categories')
        .select('*')
        .eq('organization_id', orgId)
        .eq('is_active', true)
        .order('display_order');
      setCategories(cats || []);
    } catch { setCategories([]); }
  };

  const loadSuppliers = async () => {
    try {
      const { data: supps } = await supabase.from('customers').select('id, first_name, last_name, company_name').eq('organization_id', orgId).eq('is_supplier', true).order('first_name');
      setSuppliers((supps || []).map(s => ({ id: s.id, label: s.company_name || `${s.first_name || ''} ${s.last_name || ''}`.trim() })));
    } catch { setSuppliers([]); }
  };

  const createInlineCategory = async (name: string): Promise<{ id: string; name: string } | null> => {
    if (!orgId || !name.trim()) return null;
    const nextOrder = categories.length > 0 ? Math.max(...categories.map(c => c.display_order || 0)) + 1 : 0;
    const { data, error } = await supabase
      .from('expense_categories')
      .insert({
        organization_id: orgId,
        name: name.trim(),
        is_active: true,
        display_order: nextOrder,
      })
      .select('id, name')
      .single();
    if (error) {
      console.error('Failed to create category:', error);
      return null;
    }
    return data;
  };

  const reset = () => {
    setTarget('');
    setError('');
    setOcrMatch(null);
    setOcrNote('');
    setForm({
      vehicle_id: '', category: '', description: '', provider: '',
      amount: '', gst_inclusive: false, gst_amount: '0',
      payment_method: 'EFT', bank_account: '',
      expense_date: new Date().toISOString().split('T')[0],
      reference: '', status: 'pending',
    });
  };

  const handleScanInvoice = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !orgId) return;

    setOcrLoading(true);
    setError('');
    setOcrMatch(null);
    setOcrNote('');

    try {
      // 1. Upload to documents bucket
      const ts = Date.now();
      const path = `invoices/ocr/${orgId}/${ts}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const { error: uploadErr } = await supabase.storage
        .from('documents')
        .upload(path, file, { contentType: file.type, cacheControl: '3600', upsert: false });
      if (uploadErr) throw new Error(`Upload failed: ${uploadErr.message}`);

      const { data: { publicUrl } } = supabase.storage.from('documents').getPublicUrl(path);

      // 2. Call OCR edge function
      const isPdf = file.type === 'application/pdf';
      const { data, error: fnErr } = await supabase.functions.invoke('extract-supplier-invoice', {
        body: { image_url: publicUrl, organization_id: orgId, is_pdf: isPdf },
      });
      if (fnErr) throw new Error(`OCR failed: ${fnErr.message}`);
      if (!data?.success) throw new Error(data?.error || 'OCR failed');

      const d = data.data;

      // 3. Decide expense target from vehicle match
      const hasVehicleMatch = !!d.matched_vehicle;
      const newTarget: ExpenseTarget = hasVehicleMatch ? 'vehicle' : 'overhead';
      setTarget(newTarget);

      // 4. Pre-fill form
      const gstIncluded = (d.gst_amount || 0) > 0;
      setForm((f) => ({
        ...f,
        vehicle_id: d.matched_vehicle?.id || '',
        provider: d.matched_supplier?.name || d.supplier_name || '',
        expense_date: d.invoice_date || f.expense_date,
        reference: d.invoice_number || '',
        amount: d.total_amount != null ? String(d.total_amount) : '',
        gst_inclusive: gstIncluded,
        gst_amount: gstIncluded ? String(d.gst_amount) : '0',
        description: d.description_hint || (d.line_items || []).map((l: any) => l.description).filter(Boolean).slice(0, 3).join(', '),
      }));

      // 5. Show match chips + notes
      setOcrMatch({
        supplier: d.matched_supplier?.name || (d.supplier_name ? `${d.supplier_name} (not in suppliers — will save as typed)` : undefined),
        vehicle: d.matched_vehicle?.label || (d.vehicle_rego || d.vehicle_vin || d.stock_number
          ? `Rego/VIN/Stock found but no match — pick manually`
          : undefined),
      });

      // 6. Warning if no amount
      if (d.total_amount == null) {
        setOcrNote("Couldn't read the total amount — please enter it manually.");
      }
    } catch (err: any) {
      setError(err.message || 'OCR failed');
    } finally {
      setOcrLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSave = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) { setError('Amount is required'); return; }
    if (target === 'vehicle' && !form.vehicle_id) { setError('Select a vehicle'); return; }

    setLoading(true);
    setError('');

    try {
      if (target === 'vehicle') {
        await expenseService.createExpense({
          vehicle_id: form.vehicle_id,
          cost_type: form.category || 'general_other',
          description: form.description,
          provider: form.provider,
          amount: parseFloat(form.amount),
          gst_amount: parseFloat(form.gst_amount) || 0,
          expense_date: form.expense_date,
          status: form.status as any,
          payment_method: form.payment_method,
          bank_account: form.bank_account,
          reference_number: form.reference,
        });
      } else {
        // Overhead expense — native statuses are draft/posted/cancelled + date_paid.
        // Unified UI treats date_paid IS NOT NULL as paid, regardless of posted/draft.
        const isPaid = form.status === 'paid';
        const today = new Date().toISOString().split('T')[0];
        const amount = parseFloat(form.amount);
        const gst = parseFloat(form.gst_amount) || 0;
        await supabase.from('overhead_expenses').insert({
          organization_id: orgId,
          purchase_date: form.expense_date,
          supplier_name: form.provider,
          payment_method: form.payment_method,
          bank_account: form.bank_account,
          reference: form.reference,
          notes: form.description,
          subtotal: amount - gst,
          gst_total: gst,
          total_amount: amount,
          status: 'posted',
          date_paid: isPaid ? today : null,
          is_synced_to_accounting: false,
        });
      }

      handleClose();
      onSuccess?.();
    } catch (err: any) {
      setError(err.message || 'Failed to save expense');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Expense</DialogTitle>
      <DialogContent>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          style={{ display: 'none' }}
          onChange={handleScanInvoice}
        />

        {/* Three big option cards: Vehicle / Overhead / Scan */}
        <Grid container spacing={1.5} sx={{ mt: 0.5, mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <OptionCard
              icon={<DirectionsCarIcon fontSize="medium" />}
              label="Vehicle Expense"
              hint="Tied to a specific car"
              selected={target === 'vehicle'}
              onClick={() => setTarget('vehicle')}
              color="#1976d2"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <OptionCard
              icon={<BusinessIcon fontSize="medium" />}
              label="Overhead Expense"
              hint="Rent, subs, utilities, etc."
              selected={target === 'overhead'}
              onClick={() => setTarget('overhead')}
              color="#6b7280"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <OptionCard
              icon={ocrLoading ? <CircularProgress size={22} /> : <CameraAltIcon fontSize="medium" />}
              label={ocrLoading ? 'Reading invoice…' : 'Scan Invoice'}
              hint="Upload a photo or PDF — we'll fill it in"
              selected={false}
              onClick={() => !ocrLoading && fileInputRef.current?.click()}
              color="#ea580c"
              disabled={ocrLoading || loading}
            />
          </Grid>
        </Grid>

        {error && <Box sx={{ mb: 2, mt: 1 }}><Typography color="error" variant="body2">{error}</Typography></Box>}

        {(ocrMatch || ocrNote) && (
          <Alert severity={ocrNote ? 'warning' : 'success'} icon={<CheckCircleIcon fontSize="inherit" />} sx={{ mb: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {ocrNote ? 'Partially extracted' : 'Extracted from invoice'}
              </Typography>
              {ocrMatch?.supplier && <Chip size="small" label={`Supplier: ${ocrMatch.supplier}`} color="primary" variant="outlined" />}
              {ocrMatch?.vehicle && <Chip size="small" label={`Vehicle: ${ocrMatch.vehicle}`} color="primary" variant="outlined" />}
            </Box>
            {ocrNote && <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>{ocrNote}</Typography>}
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
              Review the fields below before saving.
            </Typography>
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {/* 1. Vehicle (full width, only if vehicle expense) */}
          {target === 'vehicle' && (
            <Grid item xs={12}>
              <Autocomplete
                options={vehicles}
                getOptionLabel={(o: any) => typeof o === 'string' ? o : o.label}
                value={vehicles.find(v => v.id === form.vehicle_id) || null}
                onChange={(_, v: any) => setForm({ ...form, vehicle_id: v?.id || '' })}
                renderInput={(params) => <TextField {...params} label="Vehicle *" placeholder="Search stock..." />}
              />
            </Grid>
          )}

          {/* Category (full width now that Type is up top) */}
          <Grid item xs={12}>
            <Autocomplete
              freeSolo
              options={categories.map(c => ({ id: c.id, label: c.name }))}
              getOptionLabel={(o: any) => typeof o === 'string' ? o : o.label}
              value={categories.find(c => c.name === form.category) ? { id: '', label: form.category } : form.category || null}
              filterOptions={(options, params) =>
                filterWithAddNew(options, params.inputValue, 'label', 'category')
              }
              onChange={async (_, v: any) => {
                if (typeof v === 'string') {
                  setForm({ ...form, category: v });
                } else if (v?.__isNew && v?.__newName) {
                  const created = await createInlineCategory(v.__newName);
                  if (created) {
                    await loadCategories();
                    setForm({ ...form, category: created.name });
                  }
                } else if (v) {
                  setForm({ ...form, category: v.label });
                } else {
                  setForm({ ...form, category: '' });
                }
              }}
              onInputChange={(_, v, reason) => {
                if (reason === 'input') setForm({ ...form, category: v });
              }}
              renderInput={(params) => <TextField {...params} label="Category" placeholder="Type or select" />}
            />
          </Grid>

          {/* 3. Supplier | Date */}
          {target && (
            <>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={suppliers}
                  getOptionLabel={(o: any) => typeof o === 'string' ? o : o.label}
                  value={form.provider || null}
                  filterOptions={(options, params) =>
                    filterWithAddNew(options, params.inputValue, 'label', 'supplier')
                  }
                  onChange={(_, v: any) => {
                    if (typeof v === 'string') {
                      setForm({ ...form, provider: v });
                    } else if (v?.__isNew && v?.__newName) {
                      setSupplierPrefill(v.__newName);
                      setSupplierModalOpen(true);
                    } else if (v) {
                      setForm({ ...form, provider: v.label });
                    } else {
                      setForm({ ...form, provider: '' });
                    }
                  }}
                  onInputChange={(_, v, reason) => {
                    if (reason === 'input') setForm({ ...form, provider: v });
                  }}
                  renderOption={(props, option: any) => (
                    <li {...props} key={typeof option === 'string' ? option : option.id} style={option?.__isNew ? { color: '#1976d2', fontWeight: 600 } : undefined}>
                      {typeof option === 'string' ? option : option.label}
                    </li>
                  )}
                  renderInput={(params) => <TextField {...params} label="Supplier / Paid To" placeholder="Type or select" />}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField fullWidth type="date" label="Date" value={form.expense_date}
                  onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* 4. Amount | GST toggle */}
              <Grid item xs={12} sm={6}>
                <TextField fullWidth type="number" label="Amount *" value={form.amount}
                  onChange={(e) => {
                    const amt = e.target.value;
                    const gst = form.gst_inclusive ? (parseFloat(amt) / 11).toFixed(2) : '0';
                    setForm({ ...form, amount: amt, gst_amount: gst });
                  }}
                  InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 1 }}>
                  <Switch size="small" checked={form.gst_inclusive}
                    onChange={(e) => {
                      const on = e.target.checked;
                      const gst = on ? (parseFloat(form.amount || '0') / 11).toFixed(2) : '0';
                      setForm({ ...form, gst_inclusive: on, gst_amount: gst });
                    }}
                  />
                  <Box>
                    <Typography variant="body2">GST Inclusive</Typography>
                    {form.gst_inclusive && <Typography variant="caption" color="text.secondary">GST: ${form.gst_amount}</Typography>}
                    {!form.gst_inclusive && <Typography variant="caption" color="text.secondary">No GST</Typography>}
                  </Box>
                </Box>
              </Grid>

              {/* 5. Description (full width) */}
              <Grid item xs={12}>
                <TextField fullWidth label="Description" value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="What's this expense for?"
                />
              </Grid>

              {/* 6. Payment Method | Bank */}
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth label="Payment Method" value={form.payment_method}
                  onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                >
                  <MenuItem value="Cash">Cash</MenuItem>
                  <MenuItem value="Card">Card</MenuItem>
                  <MenuItem value="EFT">EFT</MenuItem>
                  <MenuItem value="Cheque">Cheque</MenuItem>
                  <MenuItem value="Petty Cash">Petty Cash</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField select fullWidth label="Bank Account" value={form.bank_account}
                  onChange={(e) => setForm({ ...form, bank_account: e.target.value })}
                >
                  <MenuItem value="">Select...</MenuItem>
                  {bankAccounts.map(a => <MenuItem key={a.id} value={a.label}>{a.label}</MenuItem>)}
                </TextField>
              </Grid>

              {/* 7. Reference | Status */}
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Reference" value={form.reference}
                  onChange={(e) => setForm({ ...form, reference: e.target.value })}
                  placeholder="Invoice #, receipt #"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField select fullWidth label="Status" value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                </TextField>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={loading || !target}>
          {loading ? <CircularProgress size={20} /> : 'Save Expense'}
        </Button>
      </DialogActions>

      <AddCustomerModal
        open={supplierModalOpen}
        contactType="supplier"
        prefillName={supplierPrefill}
        onClose={() => {
          setSupplierModalOpen(false);
          setSupplierPrefill('');
        }}
        onSuccess={async (newId) => {
          await loadSuppliers();
          // Find the just-created supplier in the refreshed list and select it
          const { data } = await supabase
            .from('customers')
            .select('id, first_name, last_name, company_name')
            .eq('id', newId)
            .single();
          if (data) {
            const label = data.company_name || `${data.first_name} ${data.last_name}`.trim();
            setForm((prev) => ({ ...prev, provider: label }));
          }
          setSupplierModalOpen(false);
          setSupplierPrefill('');
        }}
      />
    </Dialog>
  );
}

// ============================================
// Option card (vehicle / overhead / scan)
// ============================================
interface OptionCardProps {
  icon: React.ReactNode;
  label: string;
  hint: string;
  selected: boolean;
  onClick: () => void;
  color: string;
  disabled?: boolean;
}

function OptionCard({ icon, label, hint, selected, onClick, color, disabled }: OptionCardProps) {
  return (
    <Paper
      elevation={selected ? 4 : 0}
      onClick={disabled ? undefined : onClick}
      sx={{
        p: 1.5,
        height: '100%',
        cursor: disabled ? 'not-allowed' : 'pointer',
        border: '2px solid',
        borderColor: selected ? color : 'divider',
        bgcolor: selected ? `${color}12` : 'background.paper',
        opacity: disabled ? 0.6 : 1,
        transition: 'all 0.15s ease',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        '&:hover': disabled ? {} : {
          borderColor: color,
          bgcolor: `${color}08`,
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: selected ? color : 'text.primary' }}>
        {icon}
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
        {hint}
      </Typography>
    </Paper>
  );
}
